import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { LoadingStatus } from '../../core/domain/events/state_provider';
import {
  Dietitian,
  DietitianProps,
} from '../../data/dietitian/domain/dietitian';
import { DietitianCommands } from '../../data/dietitian/domain/dietitian_commands';
import {
  DietitianState,
  DietitianStateProvider,
} from '../../data/dietitian/domain/dietitian_state_provider';
import { MsdpGateway } from '../../data/msdp/gateway/msdp-gateway';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  constructor(
    private dietitianStateProvider: DietitianStateProvider,
    private dietitianCommands: DietitianCommands,
    private msdpGateway: MsdpGateway,
  ) {
    // Nada
  }

  async login(dietitian: Dietitian) {
    try {
      const msdpUserId = await this.msdpGateway.login(dietitian);
      if (msdpUserId) {
        this.dietitianStateProvider.setState(<DietitianState>{
          loading: LoadingStatus.LOADING,
          origin: 'DashboardService.login',
        });
        dietitian = dietitian.copyWith({
          msdpId: msdpUserId,
          msdpLinkedAt: new Date(),
        } as DietitianProps);
        dietitian = await this.dietitianCommands.saveDietitian(dietitian);
        this.dietitianStateProvider.setState(<DietitianState>{
          loading: LoadingStatus.COMPLETE,
          origin: 'DashboardService.login',
          entity: dietitian,
        });
      } else {
        console.error('Error', 'Utilisateur MonSuiviDiet Plus introuvable.');
        throw new Error('Utilisateur MonSuiviDiet Plus introuvable.');
      }
    } catch (e: any) {
      console.error('Error', e);
      this.dietitianStateProvider.setState(<DietitianState>{
        loading: LoadingStatus.COMPLETE,
        origin: 'DashboardService.login',
        entity: dietitian,
      });
      if (e instanceof HttpErrorResponse) {
        throw new Error(e.error.message);
      } else {
        throw e;
      }
    }
  }

  async signup(dietitian: Dietitian) {
    this.dietitianStateProvider.setState(<DietitianState>{
      loading: LoadingStatus.LOADING,
      origin: 'DashboardService.signup',
    });
    try {
      const msdpUserId = await this.msdpGateway.signup(dietitian);
      this.dietitianStateProvider.setState(<DietitianState>{
        loading: LoadingStatus.LOADING,
        origin: 'DashboardService.signup',
      });
      dietitian = dietitian.copyWith({
        msdpId: msdpUserId,
        msdpLinkedAt: new Date(),
      } as DietitianProps);
      dietitian = await this.dietitianCommands.saveDietitian(dietitian);
      this.dietitianStateProvider.setState(<DietitianState>{
        loading: LoadingStatus.COMPLETE,
        origin: 'DashboardService.signup',
        entity: dietitian,
      });
    } catch (e: any) {
      console.error('Error', e);
      this.dietitianStateProvider.setState(<DietitianState>{
        loading: LoadingStatus.COMPLETE,
        origin: 'DashboardService.signup',
        entity: dietitian,
      });
      if (e instanceof HttpErrorResponse) {
        throw new Error(e.error.message);
      } else {
        throw e;
      }
    }
  }

  async link(dietitian: Dietitian, throwable = false): Promise<string> {
    try {
      const response = await this.msdpGateway.link(dietitian);
      if (
        typeof response === 'object' &&
        'status' in response &&
        'response' in response &&
        response.status === 'success'
      ) {
        return response.response.url;
      } else {
        throw new Error(
          'La réponse de MonSuiviDiet Plus est incorrecte, veuillez nous contacter.',
        );
      }
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        if (e.status === 401 && !throwable) {
          await this.msdpGateway.login(dietitian);
          try {
            return await this.link(dietitian, true);
          } catch (e) {
            if (e instanceof HttpErrorResponse) {
              throw new Error(e.error.message);
            } else {
              throw e;
            }
          }
        } else {
          throw new Error(e.error.message);
        }
      } else {
        throw e;
      }
    }
  }

  async unlink(dietitian: Dietitian) {
    this.dietitianStateProvider.setState(<DietitianState>{
      loading: LoadingStatus.LOADING,
      origin: 'DashboardService.unlink',
    });
    try {
      await this.msdpGateway.unlink(dietitian);
    } catch (e) {
      console.error(e);
    }
    dietitian = dietitian.copyWith({
      msdpUser: undefined,
      msdpPassword: undefined,
      msdpId: undefined,
      msdpLinkedAt: undefined,
      msdpKey: undefined,
    } as DietitianProps);
    dietitian = await this.dietitianCommands.saveDietitian(dietitian);
    this.dietitianStateProvider.setState(<DietitianState>{
      loading: LoadingStatus.COMPLETE,
      origin: 'DashboardService.unlink',
      entity: dietitian,
    });
  }

  isLinked(dietitian: Dietitian | undefined) {
    return !!dietitian?.msdpId;
  }
}
